import { MediaType } from '../../../../../../../Chat.enum';
import ImageContent from './ImageContent/ImageContent.component';
import AudioContent from './AudioContent/AudioContent.component';
import VideoContent from './VideoContent/VideoContent.component';
import LocationContent from './LocationContent/LocationContent.component';
import FileContent from './FileContent/FileContent.component';
import StickerContent from './StickerContent/StickerContent.component';
import SharePostContent from './SharePostContent/SharePostContent.component';
import { tMessageByAttachmentType } from '../MessageBubble.type';
import ContactContent from './ContactContent/ContactContent.component.tsx';
import { PhotoIcon } from '@heroicons/react/20/solid';
import useAttachmentValidator from '../../../../../../../../../hooks/attachments/useAttachmentValidator.ts';
import { useTranslation } from 'react-i18next';
import { isLottieSticker } from '../../../../../../../lottieSticker.functions';
import useUnsupportedNoticeAnalytics from 'src/hooks/analytics/chat/useUnsupportedNoticeAnalytics.hook';
import { LOTTIE_CLIENT_NOTICE } from 'src/hooks/analytics/chat/unsupportedNoticeAnalytics.functions';

export default function MessageByAttachmentType({
  decryptedAttachment,
  messageRef,
}: tMessageByAttachmentType) {
  const { t } = useTranslation('chat/chat');
  const isLottie = isLottieSticker(decryptedAttachment);
  // INT-1691: reported here, where the notice is drawn: attachments are
  // decrypted after the bubble mounts.
  useUnsupportedNoticeAnalytics({
    eventId: messageRef.id,
    chatId: messageRef.chatID,
    createdAt: messageRef.createdAt,
    notice: isLottie ? LOTTIE_CLIENT_NOTICE : null,
  });
  // INT-1708: a Lottie sticker is a zip, so the validator's Image() probe would
  // always fail on it. Pass no URL to keep the hook unconditional without
  // probing.
  const { isValid } = useAttachmentValidator(
    isLottie ? null : decryptedAttachment.url,
    decryptedAttachment.mediaType,
  );

  // INT-1708: checked before the mediaType dispatch because the socket hands
  // these over as IMAGE and the history as STICKER. Without it the first path
  // reads "expired" and the second leaves an empty bubble.
  if (isLottie) {
    return (
      <div className="flex gap-2">
        <PhotoIcon className="w-5 h-5 shrink-0 text-primary" />
        <i>{t('lottieStickerNotSupported')}</i>
      </div>
    );
  }

  if (!isValid && decryptedAttachment.url) {
    return (
      <div className="flex gap-2">
        <PhotoIcon className="w-5 h-5 text-primary" />
        <i>{t('attachmentExpired')}</i>
      </div>
    );
  }

  // INT-1702: Messenger and Instagram stickers come with mimetype `sticker`; the
  // socket hands them over as IMAGE, so without this they show as a photo
  // until the chat reloads.
  if (decryptedAttachment.mimeType === 'sticker') {
    return <StickerContent attachment={decryptedAttachment} />;
  }
  if (decryptedAttachment.mediaType === MediaType.IMAGE) {
    return <ImageContent attachment={decryptedAttachment} />;
  }
  if (decryptedAttachment.mediaType === MediaType.CONTACT) {
    return <ContactContent attachment={decryptedAttachment} />;
  }
  if (decryptedAttachment.mediaType === MediaType.VIDEO) {
    return <VideoContent attachment={decryptedAttachment} />;
  }
  // INT-929: SHARE_POST has no top-level `url` so the `useAttachmentValidator`
  // guard above falls through (the `&& decryptedAttachment.url` predicate is
  // falsy), letting us render the link-preview card from the SHARE_POST-
  // specific fields (video_id / title / author_name / thumbnail_url).
  if (decryptedAttachment.mediaType === MediaType.SHARE_POST) {
    return <SharePostContent attachment={decryptedAttachment} />;
  }
  if (decryptedAttachment.mediaType === MediaType.AUDIO) {
    return <AudioContent attachment={decryptedAttachment} />;
  }
  if (decryptedAttachment.mediaType === MediaType.STICKER) {
    return <StickerContent attachment={decryptedAttachment} />;
  }
  if (decryptedAttachment.mediaType === MediaType.DOCUMENT) {
    return <FileContent attachment={decryptedAttachment} />;
  }
  if (decryptedAttachment.mediaType === MediaType.LOCATION) {
    return (
      <LocationContent
        latitude={decryptedAttachment.latitude ?? 0}
        longitude={decryptedAttachment.longitude ?? 0}
        name={decryptedAttachment.name}
        address={decryptedAttachment.address}
      />
    );
  }

  return <p>FILE</p>;
}
